import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Row,
    Col,
    Button,
    Input,
    Card,
    CardBody,
} from "reactstrap";
import client from "../../utils/ApiClient";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ProductImageManager = ({ isOpen, toggle, images, onUpdateImages, productId, initialView = "grid" }) => {
    const [view, setView] = useState(initialView);
    const [newImageUrl, setNewImageUrl] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [localImages, setLocalImages] = useState([]);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [addMode, setAddMode] = useState("url");
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState("");

    useEffect(() => {
        if (images) {
            setLocalImages(images);
        } else {
            setLocalImages([]);
        }
    }, [images]);

    useEffect(() => {
        if (isOpen) {
            setView(initialView);
            setNewImageUrl("");
            setImageToDelete(null);
            setAddMode("url");
            setSelectedFile(null);
            if (filePreviewUrl) {
                URL.revokeObjectURL(filePreviewUrl);
            }
            setFilePreviewUrl("");
        }
    }, [isOpen, initialView]);

    const handleDeleteClick = (index) => {
        setImageToDelete(index);
        setView("delete-confirm");
    };

    const confirmDelete = () => {
        if (imageToDelete !== null) {
            const updatedImages = [...localImages];
            updatedImages.splice(imageToDelete, 1);
            setLocalImages(updatedImages);
            onUpdateImages(updatedImages);
            setImageToDelete(null);
            setView("grid");
        }
    };

    const cancelDelete = () => {
        setImageToDelete(null);
        setView("grid");
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (filePreviewUrl) {
            URL.revokeObjectURL(filePreviewUrl);
        }
        setSelectedFile(file);
        setFilePreviewUrl(URL.createObjectURL(file));
    };

    const uploadImageFromFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const base64Content = e.target.result.split(",")[1];
                    const response = await client.post("/products/UploadProductImage", {
                        pro_id: productId,
                        base64_content: base64Content,
                        file_name: file.name
                    });
                    if (response.success) {
                        resolve(response.message);
                    } else {
                        toast.error(response.message || "Failed to upload image");
                        reject(new Error(response.message || "Failed to upload image"));
                    }
                } catch (error) {
                    const errorMessage = error.response?.data?.message || error.message || "Error uploading image";
                    toast.error(errorMessage);
                    reject(error);
                }
            };
            reader.onerror = () => reject(new Error("Failed to read file"));
            reader.readAsDataURL(file);
        });
    };

    const uploadImageToBucket = async (url) => {
        try {
            const response = await client.post("/products/UploadProductImage", {
                pro_id: productId,
                url: url
            });

            if (response.success) {
                return response.message;
            } else {
                toast.error(response.message || "Failed to upload image");
                throw new Error(response.message || "Failed to upload image");
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Error uploading image";
            toast.error(errorMessage);
            throw error;
        }
    };

    const handleAddImage = async () => {
        if (addMode === "url" && !newImageUrl) return;
        if (addMode === "file" && !selectedFile) return;

        setIsLoading(true);

        try {
            let uploadedUrl;
            if (addMode === "url") {
                uploadedUrl = await uploadImageToBucket(newImageUrl);
            } else {
                uploadedUrl = await uploadImageFromFile(selectedFile);
            }
            const updatedImages = [...localImages, uploadedUrl];
            setLocalImages(updatedImages);
            onUpdateImages(updatedImages);
            setNewImageUrl("");
            setSelectedFile(null);
            if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
            setFilePreviewUrl("");
            setView("grid");
        } catch (error) {
            console.error("Error uploading image:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getModalTitle = () => {
        switch (view) {
            case "add":
                return "Add New Image";
            case "delete-confirm":
                return "Confirm Deletion";
            default:
                return "Manage Images";
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>
                {getModalTitle()}
            </ModalHeader>
            <ModalBody>
                {view === "grid" && (
                    <Row>
                        {localImages.map((imgUrl, index) => (
                            <Col key={index} xs={6} md={4} lg={3} className="mb-4">
                                <div className="position-relative image-card">
                                    <div className="ratio ratio-1x1">
                                        <img
                                            src={imgUrl.startsWith("http") ? imgUrl : `${process.env.REACT_APP_THUMBS_URL}/products/${imgUrl}`}
                                            alt={`Product ${index}`}
                                            className="img-fluid rounded object-fit-cover"
                                        />
                                    </div>
                                    <div className="image-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded opacity-0 hover-opacity-100 transition-opacity">
                                        <Button
                                            color="danger"
                                            size="sm"
                                            className="rounded-circle"
                                            onClick={() => handleDeleteClick(index)}
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        ))}
                        <Col xs={6} md={4} lg={3} className="mb-4">
                            <div
                                className="ratio ratio-1x1 border border-dashed rounded d-flex align-items-center justify-content-center cursor-pointer hover-bg-light"
                                onClick={() => setView("add")}
                                style={{ cursor: "pointer" }}
                            >
                                <div className="text-center">
                                    <i className="ri-add-line fs-1"></i>
                                    <p className="mb-0">Add Image</p>
                                </div>
                            </div>
                        </Col>
                    </Row>
                )}

                {view === "add" && (
                    <div>
                        <div className="d-flex mb-3" style={{ borderBottom: "1px solid #dee2e6" }}>
                            <button
                                className={`btn btn-link px-3 py-2 text-decoration-none border-0 ${addMode === "url" ? "text-primary fw-semibold" : "text-muted"}`}
                                onClick={() => setAddMode("url")}
                                style={{ borderRadius: 0, borderBottom: addMode === "url" ? "2px solid var(--bs-primary)" : "2px solid transparent" }}
                            >
                                <i className="ri-link me-1"></i> URL
                            </button>
                            <button
                                className={`btn btn-link px-3 py-2 text-decoration-none border-0 ${addMode === "file" ? "text-primary fw-semibold" : "text-muted"}`}
                                onClick={() => setAddMode("file")}
                                style={{ borderRadius: 0, borderBottom: addMode === "file" ? "2px solid var(--bs-primary)" : "2px solid transparent" }}
                            >
                                <i className="ri-upload-2-line me-1"></i> Upload File
                            </button>
                        </div>

                        {addMode === "url" && (
                            <div className="text-center">
                                <Input
                                    type="text"
                                    placeholder="Enter image URL"
                                    value={newImageUrl}
                                    onChange={(e) => setNewImageUrl(e.target.value)}
                                    className="mb-3"
                                />
                                {newImageUrl && (
                                    <div className="mb-3">
                                        <p className="text-muted mb-2">Preview:</p>
                                        <img
                                            src={newImageUrl}
                                            alt="Preview"
                                            className="img-fluid rounded"
                                            style={{ maxHeight: "300px" }}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://via.placeholder.com/300?text=Invalid+Image+URL";
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {addMode === "file" && (
                            <div className="text-center">
                                <Input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
                                    onChange={handleFileChange}
                                    className="mb-3"
                                />
                                {filePreviewUrl && (
                                    <div className="mb-3">
                                        <p className="text-muted mb-2">Preview:</p>
                                        <img
                                            src={filePreviewUrl}
                                            alt="Preview"
                                            className="img-fluid rounded"
                                            style={{ maxHeight: "300px" }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="d-flex justify-content-center gap-2 mt-2">
                            <Button color="secondary" onClick={() => setView("grid")}>
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleAddImage}
                                disabled={(addMode === "url" ? !newImageUrl : !selectedFile) || isLoading}
                            >
                                {isLoading ? "Saving..." : "Save Image"}
                            </Button>
                        </div>
                    </div>
                )}

                {view === "delete-confirm" && (
                    <div className="text-center p-4">
                        <div className="avatar-md mx-auto mb-4">
                            <div className="avatar-title bg-danger-subtle text-danger rounded-circle fs-1">
                                <i className="ri-delete-bin-line"></i>
                            </div>
                        </div>
                        <h4>Are you sure?</h4>
                        <p className="text-muted">Do you really want to delete this image? This process cannot be undone.</p>
                        {imageToDelete !== null && localImages[imageToDelete] && (
                            <div className="mb-4">
                                <img
                                    src={localImages[imageToDelete].startsWith("http") ? localImages[imageToDelete] : `${process.env.REACT_APP_THUMBS_URL}/products/${localImages[imageToDelete]}`}
                                    alt="To delete"
                                    className="img-thumbnail"
                                    style={{ maxHeight: "150px" }}
                                />
                            </div>
                        )}
                        <div className="d-flex justify-content-center gap-2">
                            <Button color="light" onClick={cancelDelete}>
                                Cancel
                            </Button>
                            <Button color="danger" onClick={confirmDelete}>
                                Yes, Delete It
                            </Button>
                        </div>
                    </div>
                )}
            </ModalBody>
            <style jsx>{`
        .hover-opacity-100:hover {
          opacity: 1 !important;
        }
        .transition-opacity {
          transition: opacity 0.2s ease-in-out;
        }
        .hover-bg-light:hover {
            background-color: #f8f9fa;
        }
      `}</style>
        </Modal>
    );
};

export default ProductImageManager;
